import { LineChart } from "components/charts"
import { DataTable, Card, Title, MonthPicker, SelectInput, Icon, Text, Empty } from "components/common"
import { t } from "i18next"
import { capitalizeFirstLetter } from "utils"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import { useGetClientLeadsQuery, useLazyGetConversionProbabilityQuery } from "features/shared/api"
import Flex from "components/common/flex"
import theme from "utils/themeVars"
import { useSearchParams } from "react-router-dom"
import { getMilestoneTooltip, milestoneColorMapper } from "../../utils"

const ConversionProbability = ({ client, leadId }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryObj = Object.fromEntries([...searchParams])

  const { data: leads, isFetching: fetchingLeads } = useGetClientLeadsQuery({ clientId: client?.id })
  const [getConversionData, { data: conversiondata, isFetching }] = useLazyGetConversionProbabilityQuery()

  const getDefaultDayForMonth = monthObj =>
    monthObj
      ? monthObj.month() === dayjs().month()
        ? dayjs().date()
        : dayjs().month(monthObj.month()).daysInMonth()
      : null

  const [selectedDate, setSelectedDate] = useState(getDefaultDayForMonth(dayjs(queryObj.month)))
  const [selectedFilter, setSelectedFilter] = useState({
    month: dayjs(queryObj.month),
    inquiryId: leadId,
  })
  useEffect(() => {
    if (leads) {
      setSelectedFilter({ ...selectedFilter, inquiryId: leadId ?? leads?.[0]?.value })
      getConversionData({
        month: selectedFilter?.month?.month(),
        clientId: client?.id,
        inquiryId: leadId ?? leads?.[0]?.value,
      })
    }
  }, [leads])

  const conversionProbablityData = useMemo(() => {
    const firstList = [],
      secondList = []
    conversiondata?.staffConversionData?.map(item => {
      firstList.push({
        name: "Staff",
        conversionProbability: +item?.conversionProbability,
        date: item?.updatedAt,
        staffName: item?.staffName,
      })
    })
    conversiondata?.aiConversionData?.map(item => {
      secondList.push({
        name: "AI",
        conversionProbability: +item?.analysis?.closure_probability,
        date: item?.analysisDate,
        ...item,
        milestones: item?.milestones?.length ? item?.milestones : null,
      })
    })
    return firstList.concat(secondList)
  }, [conversiondata])
  const tableColumns = () => [
    { title: t("Metric"), dataIndex: "metric" },
    { title: t("Scale"), dataIndex: "scale" },
    { title: t("Description"), dataIndex: "description" },
  ]
  const tableData = useMemo(() => {
    if (conversiondata?.aiConversionData) {
      const data = conversiondata?.aiConversionData
      return data?.[selectedDate - 1]?.analysis?.metrics?.map(item => {
        const key = Object.keys(item)[0]
        return {
          metric: capitalizeFirstLetter(key),
          scale: item[key].score,
          description: item[key].brief,
        }
      })
    }
    return []
  }, [selectedDate, conversiondata])

  const getDayList = dateStr => {
    if (!dateStr) return []
    const day = dayjs(dateStr).date()
    const monthYear = dayjs(dateStr).startOf("month")
    return Array.from({ length: day }, (_, i) => ({
      value: i + 1,
      label: monthYear.add(i, "day").format("MMMM D, YYYY"),
    }))
  }

  const legendItems = [
    {
      title: t("Client Creation"),
      color: milestoneColorMapper["client_creation"],
    },
    {
      title: t("Contacted"),
      color: milestoneColorMapper["phone_call"],
    },
    {
      title: t("Meeting"),
      color: milestoneColorMapper["meeting_done"],
    },
    {
      title: t("Audit Review"),
      color: milestoneColorMapper["audit_review"],
    },
    {
      title: t("New Payment / Contract Signed"),
      color: milestoneColorMapper["payment_added"],
    },
    {
      title: t("Payment Rejected / Contract Cancelled"),
      color: milestoneColorMapper["payment_rejected"],
    },
  ]
  return (
    <>
      <Flex gap="8px">
        <MonthPicker
          containerWidth="150px"
          className="mbe-12"
          value={selectedFilter?.month}
          onChange={value => {
            setSelectedFilter({ ...selectedFilter, month: value })
            setSelectedDate(getDefaultDayForMonth(value))
            getConversionData({
              month: value?.month(),
              clientId: client?.id,
              inquiryId: selectedFilter?.inquiryId,
            })
          }}
          disabled={isFetching || fetchingLeads}
          disabledDate={current => current.year() !== dayjs().year() || current.month() > dayjs().month()}
        />
        <SelectInput
          className="mbe-12"
          placeholder={t("Select Lead")}
          inputLoading={fetchingLeads}
          disabled={fetchingLeads || isFetching}
          options={leads}
          value={selectedFilter?.inquiryId}
          onChange={value => {
            setSelectedFilter({ ...selectedFilter, inquiryId: value })
            setSelectedDate(getDefaultDayForMonth(selectedFilter?.month))
            getConversionData({
              month: selectedFilter?.month?.month(),
              clientId: client?.id,
              inquiryId: value,
            })
          }}
        />
      </Flex>
      {conversiondata?.aiConversionData?.length ||
      conversiondata?.staffConversionData?.length ||
      isFetching ||
      fetchingLeads ? (
        <Card className="mbe-12" title={<Title level={3}>{t("Progression")}</Title>}>
          <LineChart
            data={conversionProbablityData}
            xField="date"
            yField="conversionProbability"
            seriesField="name"
            loading={fetchingLeads || isFetching}
            xAxis={{
              label: {
                formatter: v => dayjs(v).date(),
              },
            }}
            yAxis={{
              max: 100,
              label: { formatter: v => `${v} %` },
            }}
            rawFields={["milestones", "conversionProbability", "date", "name"]}
            legend={{ position: "top-right" }}
            point={{
              size: item =>
                item?.name === "AI" && milestoneColorMapper[item?.milestones?.[0]?.milestoneTypeKey] ? 5 : 0,
              style: item => {
                if (item?.name === "AI" && item?.milestones) {
                  return {
                    fill: milestoneColorMapper[item?.milestones?.[0]?.milestoneTypeKey],
                  }
                }
              },
            }}
            animation={false}
            tooltip={{
              customContent: (title, items) => {
                if (!items || !items.length) return null
                return (
                  <div style={{ width: 350 }}>
                    {items[1]?.data?.milestones?.[0]?.milestoneTypeKey !== "client_creation" ? (
                      <>
                        <Flex justify="space-between" className="mbe-12" style={{ marginTop: "12px" }}>
                          <Text>{t("Progression")}</Text>
                          <span className="gray-800 fs14"> {title}</span>
                        </Flex>
                        <Flex vertical align="flex-start" className="mbe-12" gap="8px">
                          {items.map(item => (
                            <Flex style={{ width: "100%" }} justify="space-between" align="center">
                              <div>
                                <Icon
                                  icon={item?.name === "Staff" ? "StaffGraphIcon" : "AIGraphIcon"}
                                  color={item?.color}
                                />
                                <span className="gray-800 fs14">
                                  {item?.name == "Staff" ? (items?.[0]?.data?.staffName ?? item?.name) : item?.name}
                                </span>
                              </div>
                              <strong>
                                {+item?.value || 0}%{" "}
                                {item?.name == "AI" && !!item?.data?.deltaPercentage && (
                                  <Text
                                    className={`fs12`}
                                    style={{
                                      color:
                                        item?.data?.deltaPercentage >= 0
                                          ? theme["primary-color"]
                                          : theme["error-color"],
                                    }}
                                  >{`(${(item?.data?.deltaPercentage >= 0 ? "+" : "") + item?.data?.deltaPercentage}%)`}</Text>
                                )}
                              </strong>
                            </Flex>
                          ))}
                        </Flex>
                      </>
                    ) : null}
                    {items[0]?.name == "AI" && getMilestoneTooltip(items[0])}
                    {items[1]?.name == "AI" && getMilestoneTooltip(items[1])}
                  </div>
                )
              },
              domStyles: {
                "g2-tooltip": {
                  borderRadius: "12px",
                  boxShadow: "0px 0px 32px 0px rgba(0, 0, 0, 0.12)",
                },
              },
            }}
          />
          <Flex justify={"space-evenly"} gap={6} style={{ marginTop: "12px" }}>
            {legendItems?.map(item => (
              <Flex>
                <Icon icon="GoDotFill" size="1.2em" color={item?.color} />
                <Text type={"secondary"} className={"fs12 fw-medium"}>
                  {item?.title}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Card>
      ) : (
        <Empty
          description={t(
            "Sorry the Conversion Progression is not available as no meaningful effort has been made on the lead"
          )}
        />
      )}
      {conversiondata?.aiConversionData?.length ? (
        <>
          <SelectInput
            className="mbe-12"
            value={selectedDate}
            options={getDayList(
              conversiondata?.aiConversionData?.[conversiondata?.aiConversionData?.length - 1]?.analysisDate
            )}
            style={{ width: "50%" }}
            onChange={value => setSelectedDate(value)}
          />

          <Flex className="mbe-12" align="center" gap="4px">
            <Icon icon="MdInfoOutline" style={{ color: theme["gray800"] }} />
            <Text className="gray-800">
              {t("AI Analysis Metrics for")} {selectedDate}, {selectedFilter?.month?.format("MMMM YYYY")}
            </Text>
          </Flex>
          <DataTable stickyOffset={-24} columns={tableColumns()} data={tableData || []} />
        </>
      ) : null}
    </>
  )
}

export default ConversionProbability
